/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { font, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { sharedLabel } from './i18n'

export interface ShowMoreFooterProps {
  /** Another page is available — when false the button is not rendered. */
  hasMore: boolean
  /** A page is in flight; the button swaps to a spinner and stops accepting
   * clicks. Results already on screen must stay put while this is true. */
  isLoadingMore?: boolean
  onLoadMore: () => void
  /** Button copy. Defaults to "Show More". */
  label?: string
  /**
   * Say "No More Results" once the last page is in. Off by default: on a list
   * that only ever had one page there is no "more" for the reader to have been
   * expecting, so the line answers a question nobody asked.
   */
  showEndOfList?: boolean
  /** Set when the last page failed, so the list is not silently incomplete. */
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  'data-testid'?: string
}

/**
 * End-of-list "Show More" control for paginated lists.
 *
 * Extracted from the game list view's footer, whose styles this keeps. It owns
 * three states a caller would otherwise re-derive: a page in flight, the end of
 * the list, and a failed page. The last one matters most — without it a list
 * that lost a page looks finished, and the reader acts on a set that silently
 * excludes it.
 */
const ShowMoreFooter = ({
  hasMore,
  isLoadingMore = false,
  onLoadMore,
  label,
  showEndOfList = false,
  isError = false,
  errorMessage,
  onRetry,
  'data-testid': testId,
}: ShowMoreFooterProps) => {
  'use memo'

  if (isError) {
    return (
      <div css={noticeStyles} role='alert' data-testid={testId}>
        <span css={noticeTextStyles}>
          {sharedLabel('listLoadFailed')}
          {errorMessage ? ` — ${errorMessage}` : ''}
        </span>
        {onRetry !== undefined && (
          <button type='button' css={actionStyles} onClick={onRetry}>
            {sharedLabel('listLoadRetry')}
          </button>
        )}
      </div>
    )
  }

  if (!hasMore) {
    if (!showEndOfList) {
      return null
    }
    return (
      <div css={footerStyles} data-testid={testId}>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {sharedLabel('gameNoMoreResults')}
        </Text>
      </div>
    )
  }

  return (
    <div css={footerStyles} data-testid={testId}>
      <button
        type='button'
        css={actionStyles}
        onClick={onLoadMore}
        // Guarding on the in-flight page rather than the click: a second
        // request stacked on the first double-appends the same page.
        disabled={isLoadingMore}
        aria-busy={isLoadingMore}
      >
        {isLoadingMore ? (
          <span css={busyStyles}>
            <Spin size='small' />
            {sharedLabel('gameLoadingMore')}
          </span>
        ) : (
          label ?? capitalizeWords('Show More')
        )}
      </button>
    </div>
  )
}

export default ShowMoreFooter

// ─── Styles ──────────────────────────────────────────────────────
// Carried over from the game list view's LoadMoreFooter.

const footerStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
  min-height: 60px;
`

const busyStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const noticeStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${colors.ERROR_200};
  background: ${colors.SECONDARY_400};
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
`

const noticeTextStyles = css`
  flex: 1 1 auto;
  color: ${colors.NEUTRAL_50};
`

const actionStyles = css`
  ${font.body}
  ${weightSemiBold}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.SECONDARY_300};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
